import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback
} from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { OnboardingV3Context } from 'context/onboarding-v3'
import { Cdn } from 'utils/assets'
import { useOnboardingChecklist } from 'hooks/use-onboarding-checklist'
import {
  useChecklistRewards,
  type ChecklistRewardType
} from 'hooks/use-checklist-rewards'
import ModalCompletionOnboardingV3 from 'components/modals/modal-completion-onboarding-v3'
import {
  LER_HOME_ROOT_PATH,
  FOREST_PATH,
  FOREST_TREES_PATH,
  LEAGUE_PATH
} from 'routes/paths'
import scrollTo from 'utils/scrollTo'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import { getVisibleChecklists } from 'utils/features-control/features-control'
import * as S from './OnboardingChecklistFull.styles'

const checklistCoinImage = Cdn.getImageUrl('onboarding/checklist-coin.webp')

const CheckIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <title>Check</title>
    <path
      d="M10 3L4.5 8.5L2 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ChevronIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <title>Chevron</title>
    <path
      d="M5 3L9 7L5 11"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <title>Close</title>
    <path
      d="M18 6L6 18M6 6L18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

type OnboardingChecklistFullProps = {
  onClose?: () => void
}

const OnboardingChecklistFull = ({ onClose }: OnboardingChecklistFullProps) => {
  const history = useHistory()
  const location = useLocation()
  const { state, setShowFinalModal } = useContext(OnboardingV3Context)
  const { checklistStatus, refetch } = useOnboardingChecklist()
  const { giveReward, checkRewardStatus } = useChecklistRewards()
  const { user } = useUserInfo()

  const visibleChecklists = useMemo(() => getVisibleChecklists(user), [user])

  const [expandedItems, setExpandedItems] = useState<{
    [key: number]: boolean
  }>({
    1: false,
    2: true,
    3: false,
    4: false
  })

  const [rewardStatus, setRewardStatus] = useState<{
    reading: boolean | null
    conquests: boolean | null
    tree: boolean | null
  }>({
    reading: null,
    conquests: null,
    tree: null
  })

  const [showRewardModal, setShowRewardModal] = useState<{
    isOpen: boolean
    type: ChecklistRewardType | null
  }>({
    isOpen: false,
    type: null
  })

  const isPollingRef = useRef(false)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const checkRewards = useCallback(async () => {
    const readingReward = await checkRewardStatus('checklist_primeira_leitura')

    const rewards: {
      reading: boolean | null
      conquests: boolean | null
      tree: boolean | null
    } = {
      reading: readingReward,
      conquests: null,
      tree: null
    }

    if (visibleChecklists.showConquests) {
      rewards.conquests = await checkRewardStatus('checklist_area_conquistas')
    }

    if (visibleChecklists.showTree) {
      rewards.tree = await checkRewardStatus('checklist_plantar_arvore')
    }

    setRewardStatus(rewards)
  }, [checkRewardStatus, visibleChecklists])

  const checkChecklistAndRewards = useCallback(async () => {
    if (isPollingRef.current) {
      return
    }

    isPollingRef.current = true

    try {
      await Promise.all([refetch(), checkRewards()])
    } finally {
      isPollingRef.current = false
    }
  }, [refetch, checkRewards])

  const checklistItems = useMemo(
    () => ({
      interests: true,
      reading: checklistStatus?.firstReading ?? state.checklistItems.reading,
      conquests:
        checklistStatus?.achievementArea ?? state.checklistItems.conquests,
      tree: checklistStatus?.plantTree ?? state.checklistItems.tree
    }),
    [
      checklistStatus?.firstReading,
      checklistStatus?.achievementArea,
      checklistStatus?.plantTree,
      state.checklistItems.reading,
      state.checklistItems.conquests,
      state.checklistItems.tree
    ]
  )

  const allItemsCompleted = useMemo(() => {
    const base = checklistItems.interests && checklistItems.reading

    if (!visibleChecklists.showConquests && !visibleChecklists.showTree) {
      return base
    }

    if (visibleChecklists.showConquests && !visibleChecklists.showTree) {
      return base && checklistItems.conquests
    }

    return base && checklistItems.conquests && checklistItems.tree
  }, [checklistItems, visibleChecklists])

  const allRewardsReceived = useMemo(() => {
    const base = rewardStatus.reading === true

    if (!visibleChecklists.showConquests && !visibleChecklists.showTree) {
      return base
    }

    if (visibleChecklists.showConquests && !visibleChecklists.showTree) {
      return base && rewardStatus.conquests === true
    }

    return base && rewardStatus.conquests === true && rewardStatus.tree === true
  }, [
    rewardStatus.reading,
    rewardStatus.conquests,
    rewardStatus.tree,
    visibleChecklists
  ])

  const shouldStopPolling = useMemo(
    () => allItemsCompleted && allRewardsReceived,
    [allItemsCompleted, allRewardsReceived]
  )

  useEffect(() => {
    checkChecklistAndRewards()

    if (shouldStopPolling) {
      return
    }

    pollingIntervalRef.current = setInterval(() => {
      checkChecklistAndRewards()
    }, 20000)

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [checkChecklistAndRewards, shouldStopPolling])

  useEffect(() => {
    setRewardStatus(prev => ({
      reading: prev.reading,
      conquests: visibleChecklists.showConquests ? prev.conquests : null,
      tree: visibleChecklists.showTree ? prev.tree : null
    }))
  }, [visibleChecklists.showConquests, visibleChecklists.showTree])

  const getRewardStatusByType = (
    rewardType: ChecklistRewardType
  ): boolean | null => {
    if (rewardType === 'checklist_primeira_leitura') return rewardStatus.reading
    if (rewardType === 'checklist_area_conquistas')
      return rewardStatus.conquests
    if (rewardType === 'checklist_plantar_arvore') return rewardStatus.tree
    return null
  }

  const toggleItem = (
    itemId: number,
    itemCompleted: boolean,
    rewardType?: ChecklistRewardType
  ) => {
    if (itemCompleted && rewardType) {
      const status = getRewardStatusByType(rewardType)
      if (status === false) {
        return
      }
    }

    if (itemCompleted && rewardType) {
      const status = getRewardStatusByType(rewardType)
      if (status === true && !expandedItems[itemId]) {
        return
      }
    }

    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }))
  }

  const getRewardAmounts = (rewardType: ChecklistRewardType) => {
    if (rewardType === 'checklist_primeira_leitura') {
      return { coins: 40, waterDrops: 20, points: 10 }
    } else if (rewardType === 'checklist_area_conquistas') {
      return { coins: 30, waterDrops: 15, points: 5 }
    } else if (rewardType === 'checklist_plantar_arvore') {
      return { coins: 50, waterDrops: 25, points: 15 }
    }
    return { coins: 0, waterDrops: 0, points: 0 }
  }

  const handleViewRewards = async (rewardType: ChecklistRewardType) => {
    try {
      const amounts = getRewardAmounts(rewardType)
      await giveReward(rewardType, amounts)
      setShowRewardModal({ isOpen: true, type: rewardType })

      if (rewardType === 'checklist_primeira_leitura') {
        setRewardStatus(prev => ({ ...prev, reading: true }))
      } else if (rewardType === 'checklist_area_conquistas') {
        setRewardStatus(prev => ({ ...prev, conquests: true }))
      } else if (rewardType === 'checklist_plantar_arvore') {
        setRewardStatus(prev => ({ ...prev, tree: true }))
      }

      await refetch()
    } catch (error) {
      if (error) {
        return
      }
    }
  }

  const handleCloseRewardModal = async () => {
    setShowRewardModal({ isOpen: false, type: null })
    await refetch()
  }

  const handleViewConquistasFromModal = async () => {
    setShowRewardModal({ isOpen: false, type: null })
    await refetch()
    handleGoToConquests()
  }

  const handleViewFinalReward = () => {
    setShowFinalModal(true)
  }

  const restoreTabsVisibility = useCallback(() => {
    const tabsElement = document.getElementById('tab-nav-home')
    if (tabsElement) {
      tabsElement.style.pointerEvents = ''
      tabsElement.style.opacity = ''
      tabsElement.style.visibility = ''

      const tabButtons = tabsElement.querySelectorAll(
        'button, a, [role="button"]'
      )
      tabButtons.forEach(button => {
        const htmlButton = button as HTMLElement
        htmlButton.style.pointerEvents = ''
        htmlButton.style.opacity = ''
        htmlButton.style.visibility = ''
      })

      const headerWrapper = tabsElement.parentElement as HTMLElement
      if (headerWrapper) {
        headerWrapper.style.pointerEvents = ''
        headerWrapper.style.opacity = ''
        headerWrapper.style.visibility = ''
      }
    }
  }, [])

  const handleClose = useCallback(() => {
    restoreTabsVisibility()
    if (onClose) {
      onClose()
    }
  }, [onClose, restoreTabsVisibility])

  useEffect(() => {
    return () => {
      restoreTabsVisibility()
    }
  }, [restoreTabsVisibility])

  const scrollToRecommendedSection = (attempt = 0) => {
    const recommendedSection = document.getElementById('recommended-book-shelf')
    if (recommendedSection) {
      const scrollYSubtract = window.innerWidth < 768 ? 105 : 45
      const targetPosition =
        recommendedSection.getBoundingClientRect().top +
        window.scrollY -
        scrollYSubtract
      scrollTo(document.documentElement, targetPosition, 300)
    } else if (attempt < 10) {
      setTimeout(() => {
        scrollToRecommendedSection(attempt + 1)
      }, 100)
    }
  }

  const handleGoToRecommendedBooks = () => {
    const isOnHomePage = location.pathname === LER_HOME_ROOT_PATH

    if (isOnHomePage) {
      scrollToRecommendedSection()
    } else {
      history.push(LER_HOME_ROOT_PATH)
      setTimeout(() => {
        scrollToRecommendedSection()
      }, 300)
    }
  }

  const handleGoToConquests = () => {
    const targetPath = visibleChecklists.showTree ? FOREST_PATH : LEAGUE_PATH
    history.push(targetPath)
  }

  const handleGoToForest = () => {
    if (checklistItems.conquests) {
      history.push(FOREST_TREES_PATH)
    } else {
      history.push(FOREST_PATH)
    }
  }

  const TOTAL_PAGES = 5
  const readingPages = checklistStatus?.readingPages ?? 0
  const progress = (readingPages / TOTAL_PAGES) * 100

  const isItemLockedOpen = useCallback(
    (itemCompleted: boolean, rewardReceived: boolean | null) => {
      return itemCompleted && rewardReceived === false
    },
    []
  )

  const isItemLockedClosed = useCallback(
    (itemCompleted: boolean, rewardReceived: boolean | null) => {
      return itemCompleted && rewardReceived === true
    },
    []
  )

  const shouldShowRewardButton = (
    itemCompleted: boolean,
    rewardReceived: boolean | null
  ) => {
    return itemCompleted && rewardReceived === false
  }

  useEffect(() => {
    setExpandedItems(prev => {
      const newState = { ...prev }

      if (isItemLockedOpen(checklistItems.reading, rewardStatus.reading)) {
        newState[2] = true
      } else if (
        isItemLockedClosed(checklistItems.reading, rewardStatus.reading)
      ) {
        newState[2] = false
      }

      if (visibleChecklists.showConquests) {
        if (
          isItemLockedOpen(checklistItems.conquests, rewardStatus.conquests)
        ) {
          newState[3] = true
        } else if (
          isItemLockedClosed(checklistItems.conquests, rewardStatus.conquests)
        ) {
          newState[3] = false
        }
      }

      if (visibleChecklists.showTree) {
        if (isItemLockedOpen(checklistItems.tree, rewardStatus.tree)) {
          newState[4] = true
        } else if (isItemLockedClosed(checklistItems.tree, rewardStatus.tree)) {
          newState[4] = false
        }
      }

      return newState
    })
  }, [
    checklistItems.reading,
    checklistItems.conquests,
    checklistItems.tree,
    rewardStatus.reading,
    rewardStatus.conquests,
    rewardStatus.tree,
    visibleChecklists.showConquests,
    visibleChecklists.showTree,
    isItemLockedOpen,
    isItemLockedClosed
  ])

  return (
    <S.Container>
      <S.Header>
        <S.HeaderContent>
          <S.HeaderTitle>
            Complete suas primeiras missões na Árvore!
          </S.HeaderTitle>
          <S.HeaderDescription>
            Finalize todas as etapas e ganhe <br />
            uma <S.HeaderDescriptionBold>recompensa</S.HeaderDescriptionBold>!
          </S.HeaderDescription>
        </S.HeaderContent>
        <S.HeaderIllustration>
          <S.HeaderImage src={checklistCoinImage} alt="Moedas" />
        </S.HeaderIllustration>
        <S.CloseButton onClick={handleClose} aria-label="Fechar">
          <CloseIcon />
        </S.CloseButton>
      </S.Header>

      <S.ChecklistContent>
        <S.ChecklistItem
          $isCompleted={checklistItems.interests}
          onClick={() => toggleItem(1, checklistItems.interests)}
        >
          <S.ItemContainer>
            <S.ItemIcon $isCompleted={checklistItems.interests}>
              {checklistItems.interests && <CheckIcon />}
            </S.ItemIcon>
            <S.ItemContent>
              <S.ItemTextBlock $isCompleted={checklistItems.interests}>
                <S.ItemTitle>Escolha seus interesses</S.ItemTitle>
                {expandedItems[1] && (
                  <S.ItemDescription>
                    Escolha seus temas favoritos para nos ajudar a encontrar
                    histórias feitas para você.
                  </S.ItemDescription>
                )}
              </S.ItemTextBlock>
            </S.ItemContent>
          </S.ItemContainer>
          <S.ChevronIcon
            $isCompleted={checklistItems.interests}
            $isExpanded={expandedItems[1]}
          >
            <ChevronIcon />
          </S.ChevronIcon>
        </S.ChecklistItem>

        <S.ChecklistItem
          $isCompleted={checklistItems.reading}
          onClick={() =>
            toggleItem(2, checklistItems.reading, 'checklist_primeira_leitura')
          }
          $isLocked={isItemLockedOpen(
            checklistItems.reading,
            rewardStatus.reading
          )}
        >
          <S.ItemContainer>
            <S.ItemIcon $isCompleted={checklistItems.reading}>
              {checklistItems.reading && <CheckIcon />}
            </S.ItemIcon>
            <S.ItemContent>
              <S.ItemTextBlock $isCompleted={checklistItems.reading}>
                <S.ItemTitle>Faça sua primeira leitura</S.ItemTitle>
                {expandedItems[2] && (
                  <S.ItemDescription>
                    Vamos encontrar um livro que combine com você! Que tal
                    começar pela sua prateleira de recomendações?
                  </S.ItemDescription>
                )}
              </S.ItemTextBlock>
              {expandedItems[2] && !checklistItems.reading && (
                <>
                  <S.ProgressBarWrapper>
                    <S.ProgressBarContainer>
                      <S.ProgressBarFill $progress={progress} />
                    </S.ProgressBarContainer>
                    <S.ProgressText>
                      {readingPages}/{TOTAL_PAGES} páginas
                    </S.ProgressText>
                  </S.ProgressBarWrapper>
                  <S.ItemButton
                    onClick={e => {
                      e.stopPropagation()
                      handleGoToRecommendedBooks()
                    }}
                  >
                    <S.ItemButtonText>Ver livros recomendados</S.ItemButtonText>
                  </S.ItemButton>
                </>
              )}
              {expandedItems[2] &&
                shouldShowRewardButton(
                  checklistItems.reading,
                  rewardStatus.reading
                ) && (
                  <S.ItemButton
                    $isReward
                    onClick={e => {
                      e.stopPropagation()
                      handleViewRewards('checklist_primeira_leitura')
                    }}
                  >
                    <S.ItemButtonText $isReward>
                      Ver recompensas
                    </S.ItemButtonText>
                  </S.ItemButton>
                )}
            </S.ItemContent>
          </S.ItemContainer>
          {!isItemLockedOpen(checklistItems.reading, rewardStatus.reading) &&
            !isItemLockedClosed(
              checklistItems.reading,
              rewardStatus.reading
            ) && (
              <S.ChevronIcon
                $isCompleted={checklistItems.reading}
                $isExpanded={expandedItems[2]}
              >
                <ChevronIcon />
              </S.ChevronIcon>
            )}
        </S.ChecklistItem>

        {visibleChecklists.showConquests && (
          <S.ChecklistItem
            $isCompleted={checklistItems.conquests}
            onClick={() =>
              toggleItem(
                3,
                checklistItems.conquests,
                'checklist_area_conquistas'
              )
            }
            $isLocked={isItemLockedOpen(
              checklistItems.conquests,
              rewardStatus.conquests
            )}
          >
            <S.ItemContainer>
              <S.ItemIcon $isCompleted={checklistItems.conquests}>
                {checklistItems.conquests && <CheckIcon />}
              </S.ItemIcon>
              <S.ItemContent>
                <S.ItemTextBlock $isCompleted={checklistItems.conquests}>
                  <S.ItemTitle>Conheça a área de Conquistas</S.ItemTitle>
                  {expandedItems[3] && (
                    <S.ItemDescription>
                      Explore a Floresta e acompanhe o crescimento das suas
                      árvores.
                    </S.ItemDescription>
                  )}
                </S.ItemTextBlock>
                {expandedItems[3] && !checklistItems.conquests && (
                  <S.ItemButton
                    onClick={e => {
                      e.stopPropagation()
                      handleGoToConquests()
                    }}
                  >
                    <S.ItemButtonText>Ir para Conquistas</S.ItemButtonText>
                  </S.ItemButton>
                )}
                {expandedItems[3] &&
                  shouldShowRewardButton(
                    checklistItems.conquests,
                    rewardStatus.conquests
                  ) && (
                    <S.ItemButton
                      $isReward
                      onClick={e => {
                        e.stopPropagation()
                        handleViewRewards('checklist_area_conquistas')
                      }}
                    >
                      <S.ItemButtonText $isReward>
                        Ver recompensas
                      </S.ItemButtonText>
                    </S.ItemButton>
                  )}
              </S.ItemContent>
            </S.ItemContainer>
            {!isItemLockedOpen(
              checklistItems.conquests,
              rewardStatus.conquests
            ) &&
              !isItemLockedClosed(
                checklistItems.conquests,
                rewardStatus.conquests
              ) && (
                <S.ChevronIcon
                  $isCompleted={checklistItems.conquests}
                  $isExpanded={expandedItems[3]}
                >
                  <ChevronIcon />
                </S.ChevronIcon>
              )}
          </S.ChecklistItem>
        )}

        {visibleChecklists.showTree && (
          <S.ChecklistItem
            $isCompleted={checklistItems.tree}
            onClick={() =>
              toggleItem(4, checklistItems.tree, 'checklist_plantar_arvore')
            }
            $isLocked={isItemLockedOpen(checklistItems.tree, rewardStatus.tree)}
          >
            <S.ItemContainer>
              <S.ItemIcon $isCompleted={checklistItems.tree}>
                {checklistItems.tree && <CheckIcon />}
              </S.ItemIcon>
              <S.ItemContent>
                <S.ItemTextBlock $isCompleted={checklistItems.tree}>
                  <S.ItemTitle>Plante uma árvore</S.ItemTitle>
                  {expandedItems[4] && (
                    <S.ItemDescription>
                      Que tal plantar uma árvore e ajudar a Floresta a
                      florescer?
                    </S.ItemDescription>
                  )}
                </S.ItemTextBlock>
                {expandedItems[4] && !checklistItems.tree && (
                  <S.ItemButton
                    onClick={e => {
                      e.stopPropagation()
                      handleGoToForest()
                    }}
                  >
                    <S.ItemButtonText>Ir para a Floresta</S.ItemButtonText>
                  </S.ItemButton>
                )}
                {expandedItems[4] &&
                  shouldShowRewardButton(
                    checklistItems.tree,
                    rewardStatus.tree
                  ) && (
                    <S.ItemButton
                      $isReward
                      onClick={e => {
                        e.stopPropagation()
                        handleViewRewards('checklist_plantar_arvore')
                      }}
                    >
                      <S.ItemButtonText $isReward>
                        Ver recompensas
                      </S.ItemButtonText>
                    </S.ItemButton>
                  )}
              </S.ItemContent>
            </S.ItemContainer>
            {!isItemLockedOpen(checklistItems.tree, rewardStatus.tree) &&
              !isItemLockedClosed(checklistItems.tree, rewardStatus.tree) && (
                <S.ChevronIcon
                  $isCompleted={checklistItems.tree}
                  $isExpanded={expandedItems[4]}
                >
                  <ChevronIcon />
                </S.ChevronIcon>
              )}
          </S.ChecklistItem>
        )}
      </S.ChecklistContent>

      {allItemsCompleted && allRewardsReceived && (
        <S.FinalButtonContainer>
          <S.FinalButton onClick={handleViewFinalReward}>
            Ver recompensa
          </S.FinalButton>
        </S.FinalButtonContainer>
      )}

      <S.HomeIndicator>
        <S.HomeIndicatorBar />
      </S.HomeIndicator>

      {showRewardModal.isOpen && showRewardModal.type && (
        <ModalCompletionOnboardingV3
          isOpen={showRewardModal.isOpen}
          onClose={handleCloseRewardModal}
          onViewConquistas={handleViewConquistasFromModal}
          rewardType={showRewardModal.type}
        />
      )}
    </S.Container>
  )
}

export default OnboardingChecklistFull
